/**
 * Prose for the AI Access Checker per-domain pages.
 *
 * The per-domain pages exist to be read by people who searched for a specific
 * domain and by the assistants that answer "does example.com block AI
 * crawlers?". Both want the same thing: named entities and a stated result in
 * plain sentences, rendered server-side rather than assembled after hydration.
 *
 * Pure and English-only. The interactive result card stays translated; this
 * narration is an English-only surface, matching the pages it serves.
 */

import type { FreeToolSummary } from "@marketing/shared/lib/free-tool-summary";
import type { AiAccessCheckResult, AipvsResult } from "@repo/core";

export type { FreeToolSummary };

/** The most bot names we spell out before switching to a count. */
const MAX_NAMED_BOTS = 6;

/** "A", "A and B", "A, B and C". */
function joinNames(names: string[]): string {
	if (names.length === 0) {
		return "";
	}
	if (names.length === 1) {
		return names[0];
	}
	return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** Spell out up to six bot names, then fall back to "and N more". */
function listBots(bots: ReadonlyArray<{ name: string }>): string | null {
	const names = bots.map((bot) => bot.name).filter(Boolean);
	if (names.length === 0) {
		return null;
	}
	if (names.length <= MAX_NAMED_BOTS) {
		return joinNames(names);
	}
	const extra = names.length - MAX_NAMED_BOTS;
	return `${names.slice(0, MAX_NAMED_BOTS).join(", ")} and ${extra} more`;
}

/** Every blocked bot across the three groups, de-duplicated by name. */
function allBlockedBots(report: AiAccessCheckResult): Array<{ name: string }> {
	const { aiVisitors, aiModelTrainers } = report.analysis.blockedBots;
	const aiSearchIndexers = report.analysis.blockedBots.aiSearchIndexers ?? [];
	const seen = new Set<string>();
	const combined: Array<{ name: string }> = [];
	for (const bot of [
		...aiVisitors,
		...aiSearchIndexers,
		...aiModelTrainers,
	]) {
		if (!bot.name || seen.has(bot.name)) {
			continue;
		}
		seen.add(bot.name);
		combined.push(bot);
	}
	return combined;
}

/** The host serving robots.txt, when it is not the domain itself. */
function differingRobotsHost(report: AiAccessCheckResult): string | null {
	if (!report.robotsTxtUrl) {
		return null;
	}
	let host: string;
	try {
		host = new URL(report.robotsTxtUrl).host.toLowerCase();
	} catch {
		return null;
	}
	const bare = host.replace(/^www\./, "");
	return bare === report.domain.toLowerCase() ? null : host;
}

const STATUS_SENTENCE: Record<
	AiAccessCheckResult["analysis"]["status"],
	string
> = {
	green: "allows the AI crawlers and assistants Spyglasses checks for",
	yellow: "blocks some AI crawlers while leaving others free to fetch its pages",
	red: "stops some AI assistants from opening its pages live while they answer",
};

const STATUS_WORD: Record<AiAccessCheckResult["analysis"]["status"], string> = {
	green: "open",
	yellow: "partly restricted",
	red: "blocked",
};

function aipvsSentence(domain: string, aipvs: AipvsResult): string {
	return `Spyglasses scores ${domain} at ${aipvs.aipvs.toFixed(1)} on the AI Placement Value Score, which puts it in tier ${aipvs.tier}, ${aipvs.tierLabel}.`;
}

/**
 * Narrate a robots.txt AI access check for one domain.
 *
 * @param report the access check, already normalized to a canonical domain
 * @param aipvs the domain's placement value score, when it resolved
 */
export function buildAiAccessSummary(
	report: AiAccessCheckResult,
	aipvs?: AipvsResult | null,
): FreeToolSummary {
	const domain = report.domain;
	const status = report.analysis.status;
	const sentences: string[] = [];

	// robots.txt couldn't be loaded: there is no verdict to narrate, and the
	// three-valued status has none either.
	if (report.robotsTxtState === "unreachable" && !report.cloudflareBlocked) {
		sentences.push(
			`Spyglasses couldn't load the robots.txt file for ${domain}, so it can't say which AI crawlers the site allows. This is often a short server problem, so the check is worth running again in a few minutes.`,
		);
		if (aipvs) {
			sentences.push(aipvsSentence(domain, aipvs));
		}
		const facts = [
			`Spyglasses couldn't load the robots.txt file for ${domain} when it last checked.`,
			`Spyglasses has no AI crawler access verdict for ${domain} until robots.txt loads.`,
			`A missing robots.txt file allows every crawler, but a file that fails to load can't be read, so there is nothing to report yet.`,
		];
		if (aipvs) {
			facts.push(
				`${domain} scores ${aipvs.aipvs.toFixed(1)} on the AI Placement Value Score, tier ${aipvs.tier} (${aipvs.tierLabel}).`,
			);
		}
		return { paragraph: sentences.join(" "), facts };
	}

	// 1. How we got (or failed to get) the rules.
	if (report.cloudflareBlocked) {
		const protection = report.cloudflareProtectionType
			? ` (${report.cloudflareProtectionType})`
			: "";
		sentences.push(
			`Cloudflare blocks automated requests to ${domain}${protection}, so the AI crawler rules in its robots.txt could not be read directly.`,
		);
	} else if (!report.robotsTxtFound) {
		sentences.push(
			`${domain} does not publish a robots.txt file, so nothing in robots.txt restricts which AI crawlers may fetch its pages.`,
		);
	} else {
		const otherHost = differingRobotsHost(report);
		const served = otherHost
			? ` at ${report.robotsTxtUrl}, which is served from ${otherHost} rather than ${domain}`
			: ` at ${report.robotsTxtUrl}`;
		sentences.push(
			`${domain} publishes a robots.txt file${served}, and Spyglasses read it to see which AI crawlers and assistants it lets in.`,
		);
	}

	// 2. The verdict, in plain words.
	sentences.push(
		`Its access status is ${status}: ${domain} ${STATUS_SENTENCE[status]}.`,
	);

	// 3. Named agents, split by what blocking them costs.
	const visitors = listBots(report.analysis.blockedBots.aiVisitors);
	sentences.push(
		visitors
			? `The AI assistants it blocks from fetching pages at answer time are ${visitors}.`
			: "No AI assistant is blocked from fetching its pages at answer time.",
	);

	const indexers = listBots(
		report.analysis.blockedBots.aiSearchIndexers ?? [],
	);
	sentences.push(
		indexers
			? `The AI search crawlers it blocks are ${indexers}. These build an assistant's own search index, so its pages may be left out of those search results, though assistants also find pages in other ways and sites like this are often still cited.`
			: "It blocks no AI search crawlers.",
	);

	const trainers = listBots(report.analysis.blockedBots.aiModelTrainers);
	sentences.push(
		trainers
			? `The AI model trainers it blocks are ${trainers}.`
			: "It blocks no AI model trainers.",
	);

	if (report.analysis.hasWildcardDisallow) {
		sentences.push(
			"A wildcard Disallow rule covers every crawler that is not named explicitly, so an unnamed AI agent is blocked by default.",
		);
	}

	if (aipvs) {
		sentences.push(aipvsSentence(domain, aipvs));
	}

	// Facts: the first three are always available, so the list is never thin.
	const facts: string[] = [];
	const blocked = listBots(allBlockedBots(report));
	if (blocked) {
		facts.push(`${domain} blocks ${blocked} in robots.txt.`);
	} else if (report.robotsTxtFound) {
		facts.push(
			`${domain} does not block any of the AI crawlers Spyglasses checks in its robots.txt.`,
		);
	} else {
		facts.push(
			`${domain} has no robots.txt file, so no AI crawler is blocked by robots rules.`,
		);
	}

	facts.push(
		report.robotsTxtFound
			? `${domain} serves its robots.txt from ${report.robotsTxtUrl}.`
			: `No robots.txt file was found for ${domain}.`,
	);

	facts.push(
		`Spyglasses rates AI crawler access for ${domain} as ${STATUS_WORD[status]}.`,
	);

	if (report.cloudflareBlocked) {
		facts.push(
			`Cloudflare blocks automated requests to ${domain}, so AI crawlers can be stopped before they ever reach its robots.txt rules.`,
		);
	}

	if (report.analysis.hasWildcardDisallow) {
		facts.push(
			`${domain} has a wildcard Disallow rule, which blocks any AI crawler it does not name explicitly.`,
		);
	}

	if (aipvs) {
		facts.push(
			`${domain} scores ${aipvs.aipvs.toFixed(1)} on the AI Placement Value Score, tier ${aipvs.tier} (${aipvs.tierLabel}).`,
		);
	}

	return { paragraph: sentences.join(" "), facts: facts.slice(0, 5) };
}
